import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import {
    Box,
    TextField,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    FormHelperText,
    Paper,
    Typography,
    CircularProgress,
    Chip,
} from "@mui/material";
import { userDetailAction } from "../asyncActions/userActions";
import SubmitButton from "../UI/SubmitButton";
import { useLocation, useParams } from "react-router-dom";
import { setModal } from "../store/modalSlice";
import EntryButton from "../UI/EntryButton";
import { clearUserDetail } from "../store/userSlice";
import { allWarehouseListAction } from "../asyncActions/warhouseActions";

function UserEditPage() {
    const dispatch = useDispatch();
    const { branchList, warehouseList, dataLoad, userDetail } = useSelector(
        (state) => state.user
    );
    const location = useLocation();
    const { id } = useParams();

    const type = useMemo(() => {
        if (location.pathname.includes("create")) return "create";
        if (location.pathname.includes("edit")) return "edit";
        return "none";
    }, [location.pathname]);

    useEffect(() => {
        dispatch(allWarehouseListAction());
        if (type === "edit") {
            dispatch(userDetailAction(id));
            return () => dispatch(clearUserDetail());
        }
        return () => reset();
    }, [dispatch, id, type]);

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        watch,
        reset,
    } = useForm();

    const branchGetter = useMemo(() => {
        return Array.isArray(userDetail.branches) && userDetail.branches.length === 1
            ? userDetail.branches[0].id
            : 0;
    }, [userDetail.user, userDetail.branches]);

    const branchesGetter = useMemo(() => {
        return Array.isArray(userDetail.branches)
            ? userDetail.branches.map((b) => b.id)
            : [];
    }, [userDetail.user, userDetail.branches]);

    const warhouseGetter = useMemo(() => {
        return Array.isArray(userDetail.warhouses) && userDetail.warhouses.length === 1
            ? userDetail.warhouses[0].id
            : 0;
    }, [userDetail.user, userDetail.warhouses]);

    useEffect(() => {
        if (type === "edit" && userDetail?.user) {
            setValue("username", userDetail.user.username || "");
            setValue("role", userDetail.user.role || "");
            setValue("branch", branchGetter || "");
            setValue("branches", branchesGetter || []);
            setValue("warhouse", warhouseGetter || "");
        }
    }, [
        type,
        userDetail,
        setValue,
        branchGetter,
        branchesGetter,
        warhouseGetter,
    ]);

    const password = watch("password");
    const role_value = watch("role");
    const branches_value = watch("branches");
    const branch_value = watch("branch");
    const warhouse_value = watch("warhouse");

    // Сброс зависимых полей при смене роли (подстраховка к onChange)
    useEffect(() => {
        if (role_value === 6) {
            setValue("branch", "");
        } else if (role_value === 1) {
            setValue("branches", []);
        } else {
            setValue("branch", "");
            setValue("branches", []);
        }
    }, [role_value, setValue]);

    const onSubmit = (data) => {
        if (type === "create") {
            const params = {
                username: data.username,
                password: data.password,
                role: data.role,
            };
            if (data.role === 6 && Array.isArray(data.branches) && data.branches.length) {
                params.branches = data.branches;
            } else if (data.branch) {
                params.branch = data.branch;
            }
            if (data.warhouse) {
                params.warhouse = data.warhouse;
            }
            dispatch(setModal({ active: true, content: "userCreate", data: params }));
        } else if (type === "edit") {
            const params = {
                id: id,
                body: {
                    username: data.username,
                    role: data.role,
                },
            };
            if (data.password) params.body.password = data.password;

            if (data.role === 6 && Array.isArray(data.branches) && data.branches.length) {
                params.body.branches = data.branches;
            } else if (data.branch) {
                params.body.branch = data.branch;
            }
            if (data.warhouse) params.body.warhouse = data.warhouse;

            dispatch(setModal({ active: true, content: "userEdit", data: params }));
        }
    };

    const handleDelete = () => {
        dispatch(
            setModal({
                active: true,
                content: "userDelete",
                data: { username: userDetail.user.username, id },
            })
        );
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "25px" }}>
            {dataLoad ? (
                <CircularProgress />
            ) : (
                <>
                    {type === "create" ? (
                        <Typography variant="h5" align="center">
                            Add User
                        </Typography>
                    ) : (
                        <Typography variant="h5" align="center">
                            Editting <span style={{ color: "orange" }}>{userDetail.user.username}</span>
                        </Typography>
                    )}
                    <Paper sx={{ padding: 2, width: "100%", maxWidth: "600px" }}>
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            style={{ display: "flex", flexDirection: "column", gap: "25px" }}
                        >
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "40px",
                                }}
                            >
                                {/* Username */}
                                <TextField
                                    label="Username"
                                    {...register("username", { required: "Username is required" })}
                                    error={Boolean(errors.username)}
                                    helperText={errors.username?.message}
                                    fullWidth
                                />

                                {/* Password */}
                                <TextField
                                    label={type === "edit" ? "Reset password" : "Password"}
                                    type="password"
                                    {...register("password", {
                                        required: { value: type !== "edit", message: "Passowrd is required" },
                                    })}
                                    error={Boolean(errors.password)}
                                    helperText={errors.password?.message}
                                    fullWidth
                                />

                                {/* Role (Controller) */}
                                <FormControl fullWidth error={Boolean(errors.role)}>
                                    <InputLabel>Role</InputLabel>
                                    <Controller
                                        name="role"
                                        control={control}
                                        rules={{ required: "Role is required" }}
                                        render={({ field }) => (
                                            <Select
                                                label="Role"
                                                value={field.value ?? ""}
                                                onChange={(e) => {
                                                    const val = Number(e.target.value);
                                                    field.onChange(val);
                                                    // сбрасываем зависимые поля
                                                    setValue("branch", "");
                                                    setValue("branches", []);
                                                    setValue("warhouse", "");
                                                }}
                                            >
                                                <MenuItem value={1}>Sales Man</MenuItem>
                                                <MenuItem value={2}>Warhouse Man</MenuItem>
                                                <MenuItem value={3}>Supplier</MenuItem>
                                                <MenuItem value={4}>Kitchen</MenuItem>
                                                <MenuItem value={5}>Manager</MenuItem>
                                                <MenuItem value={6}>Accountant</MenuItem>
                                            </Select>
                                        )}
                                    />
                                    <FormHelperText>{errors.role?.message}</FormHelperText>
                                </FormControl>

                                {/* Confirm password */}
                                <TextField
                                    label="Confirm password"
                                    type="password"
                                    {...register("confirmPassword", {
                                        validate: (v) => v === password || "Passwords do not match",
                                    })}
                                    error={Boolean(errors.confirmPassword)}
                                    helperText={errors.confirmPassword?.message}
                                    fullWidth
                                />

                                {/* Branch (single) — только роль 1 */}
                                {role_value === 1 && (
                                    <FormControl fullWidth error={Boolean(errors.branch)}>
                                        <InputLabel>Branch</InputLabel>
                                        <Controller
                                            name="branch"
                                            control={control}
                                            rules={{
                                                validate: (v) =>
                                                    role_value !== 1 || v ? true : "Branch is required",
                                            }}
                                            render={({ field }) => (
                                                <Select
                                                    label="Branch"
                                                    value={field.value ?? ""}
                                                    onChange={(e) => field.onChange(Number(e.target.value))}
                                                >
                                                    {branchList &&
                                                        branchList
                                                            .filter((b) => !b.isKitchen)
                                                            .map((b) => (
                                                                <MenuItem key={b.id} value={b.id}>
                                                                    {b.name}
                                                                </MenuItem>
                                                            ))}
                                                </Select>
                                            )}
                                        />
                                        <FormHelperText>{errors.branch?.message}</FormHelperText>
                                    </FormControl>
                                )}

                                {/* Branches (multiple) — роль 6 */}
                                {role_value === 6 && (
                                    <FormControl fullWidth error={Boolean(errors.branches)}>
                                        <InputLabel>Branches</InputLabel>
                                        <Controller
                                            name="branches"
                                            control={control}
                                            rules={{
                                                validate: (arr) =>
                                                    role_value !== 6 ||
                                                    (Array.isArray(arr) && arr.length > 0) ||
                                                    "Select at least one branch",
                                            }}
                                            render={({ field }) => (
                                                <Select
                                                    multiple
                                                    label="Branches"
                                                    value={field.value || []}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        const next = Array.isArray(val) ? val : [val];
                                                        // нормализация к числам
                                                        field.onChange(
                                                            next.map((v) => (typeof v === "string" ? Number(v) : v))
                                                        );
                                                    }}
                                                    renderValue={(selected) => (
                                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                            {(selected || []).map((id) => {
                                                                const item =
                                                                    (branchList || []).find((b) => b.id === id) || {};
                                                                return <Chip key={id} label={item.name || id} />;
                                                            })}
                                                        </Box>
                                                    )}
                                                >
                                                    {branchList &&
                                                        branchList
                                                            .filter((b) => !b.isKitchen)
                                                            .map((b) => (
                                                                <MenuItem key={b.id} value={b.id}>
                                                                    {b.name}
                                                                </MenuItem>
                                                            ))}
                                                </Select>
                                            )}
                                        />
                                        <FormHelperText>{errors.branches?.message}</FormHelperText>
                                    </FormControl>
                                )}

                                {/* Warhouse — роль 2 */}
                                {role_value === 2 && (
                                    <FormControl fullWidth error={Boolean(errors.warhouse)}>
                                        <InputLabel>Warhouse</InputLabel>
                                        <Controller
                                            name="warhouse"
                                            control={control}
                                            rules={{
                                                validate: (v) =>
                                                    role_value !== 2 || v ? true : "Warhouse is required",
                                            }}
                                            render={({ field }) => (
                                                <Select
                                                    label="Warhouse"
                                                    value={field.value ?? ""}
                                                    onChange={(e) => field.onChange(Number(e.target.value))}
                                                >
                                                    {warehouseList &&
                                                        warehouseList.map((w) => (
                                                            <MenuItem key={w.id} value={w.id}>
                                                                {w.name}
                                                            </MenuItem>
                                                        ))}
                                                </Select>
                                            )}
                                        />
                                        <FormHelperText>{errors.warhouse?.message}</FormHelperText>
                                    </FormControl>
                                )}
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    width: "100%",
                                }}
                            >
                                {type === "edit" && (
                                    <EntryButton onClick={handleDelete}>Delete</EntryButton>
                                )}
                                <SubmitButton type="submit">Submit</SubmitButton>
                            </Box>
                        </form>
                    </Paper>
                </>
            )}
        </Box>
    );
}

export default UserEditPage;
